import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { Environment } from '@environments/interface.environment';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { KpiChartGroup } from '../models/kpi-chart-group';

@Injectable({
  providedIn: 'root'
})
export class KpiChartGroupService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private cache: CacheService,
              private httpHelper: HttpHelper) { }

  private readonly cacheKey = 'ChartGroupService-getKpiChartGroups';
  private readonly activeCacheKey = 'ActiveChartGroupService-getKpiChartGroups';
  public useCache = true;
  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  getActiveKpiChartGroups$: Observable<Array<KpiChartGroup>>;
  getAllKpiChartGroups$: Observable<Array<KpiChartGroup>>;

  public reload() {
    this.reload$.next();
    this.getActiveKpiChartGroups$ = null;
    this.getAllKpiChartGroups$ = null;
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKey);
    }
    this.refresh$.next(null);
  }

  public reloadSelf() {
    this.getActiveKpiChartGroups$ = null;
    this.getAllKpiChartGroups$ = null;
  }

  getAllKPIChartGroups(): Observable<Array<KpiChartGroup>> {
    if (this.getAllKpiChartGroups$) {
      return this.getAllKpiChartGroups$;
    } else {
      this.getAllKpiChartGroups$ = this.http.get<Array<KpiChartGroup>>(`${Constants.API_ENDPOINT}/chartgroups`)
        .pipe(
          map(response => {
            console.log(`fetched result getAllKpiChartGroups: `, response);
            return response;
          }),
          tap(data => {
            this.useCache && this.cache.set(`${this.cacheKey}-all`, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllKPIChartGroups', result: new Array<KpiChartGroup>(), rethrow: true, cacheKey: this.cacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<KpiChartGroup>>(`${this.cacheKey}-all`);
        if (cachedData) {
          return concat(of(cachedData), this.getAllKpiChartGroups$);
        }
      }
      return this.getAllKpiChartGroups$;
    }
  }

  getActiveKPIChartGroups(): Observable<Array<KpiChartGroup>> {
    if (this.getActiveKpiChartGroups$) {
      return this.getActiveKpiChartGroups$;
    } else {
      this.getActiveKpiChartGroups$ = this.http.get<Array<KpiChartGroup>>(`${Constants.API_ENDPOINT}/chartgroups/active`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(this.activeCacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getActiveKPIChartGroups', result: new Array<KpiChartGroup>(), rethrow: true, cacheKey: this.activeCacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<KpiChartGroup>>(this.activeCacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getActiveKpiChartGroups$);
        }
      }
      return this.getActiveKpiChartGroups$;
    }
  }

  getKpiChartGroup(id: number): Observable<KpiChartGroup> {
    return this.http.get<KpiChartGroup>(`${Constants.API_ENDPOINT}/chartgroups/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result getKpiChartGroup: `, response);
          return response;
        }));
  }

  updateKpiChartGroup(id: number, kpiChartGroup: KpiChartGroup): Observable<KpiChartGroup> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<KpiChartGroup>(`${Constants.API_ENDPOINT}/chartgroups/${id}`, kpiChartGroup, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return kpiChartGroup;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateKpiChartGroup', result: null, rethrow: true })));

    return fetchOperation;
  }

  deleteKPIChartGroup(id: number): Observable<KpiChartGroup> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<KpiChartGroup>(`${Constants.API_ENDPOINT}/chartgroups/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteKPIChartGroup', result: null, rethrow: true })));

    return fetchOperation;
  }

  addKpiChartGroup(kpiChartGroup: KpiChartGroup): Observable<KpiChartGroup> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<KpiChartGroup>(`${Constants.API_ENDPOINT}/chartgroups`, kpiChartGroup, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`added result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'addKpiChartGroup', result: null, rethrow: true })));

    return fetchOperation;
  }
}
